import { createContext, useState, useContext, useEffect } from "react";
import { ToastContext } from "cai-fusion";
import { useUserProfile } from "./UserProfileContext";
import { useSymphonyApiService } from "../../../hooks/useSymphonyApi";
import { useNavigate } from "react-router-dom";
import env from "../../../configs/envConfig";
import { debugLog } from "../../../configs/loggingConfig";
const DataStoreContext = createContext();

function DataStoreProvider({ children }) {
    const apiServiceClient = useSymphonyApiService("maestro", "v3");
    const { userProfile } = useUserProfile();
    const { createToast } = useContext(ToastContext);

    const [dataStoreId, setDataStoreId] = useState(undefined);
    const [currFolderId, setCurrFolderId] = useState(undefined);
    // A list of folder items, used to display the file path at the top of the file view page.
    const [currPath, setCurrPath] = useState([]);

    // All data stores as confined by the search criteria.
    const [dataStores, setDataStores] = useState([]); // List of DataStores
    // All, every.
    const [allDataStores, setAllDataStores] = useState([]);

    const [dataStore, setDataStore] = useState(null); // Single DataStore (the user's current chosen one)
    // A list of all items (file & folder) at a given path.
    const [itemList, setItemList] = useState([]);
    const [folderList, setFolderList] = useState([]); // Data store's folder list
    const [sharedUsers, setSharedUsers] = useState([]); //used to show what users (and their role) for a given DataStore
    //use sharedUsers.membership to get role

    // Should be toggled when UI needs to show a loading state.
    const [isLoading, setIsLoading] = useState(false);
    // Text used for a generic activity tracker.
    const [loadingText, setLoadingText] = useState("");

    // List of all text notes
    const [textNotes, setTextNotes] = useState([]);
    // The currently selected text note.
    const [textNote, setTextNote] = useState({});

    const [usersharedUsership, setUsersharedUsership] = useState(0);
    const [canShare, setCanShare] = useState(true);
    const membershipLevels = {
        1: "Manager",
        2: "Contribute",
        3: "View Only"
    };

    const navigate = useNavigate();

    useEffect(() => {
        getDataStores();
    }, []);

    useEffect(() => {
        const theUser = sharedUsers.find(user => user.userProfileId === userProfile.userProfileId)
        const userMembership = theUser?.membership
        setCanShare(userMembership < 2);
    }, [userProfile, usersharedUsership, sharedUsers])

    // When ID is updated
    useEffect(() => {
        // If we already have the object, what's the point in refetching it?
        if (dataStoreId && dataStore?.id !== dataStoreId) {
            fetchDataStore(dataStoreId);
        }
        else {
            setDataStore(undefined);
        }
    }, [dataStoreId]);


    /// ----------------------
    /// New V3 Functions
    /// Are they out of place? Yes. We'll rearrange them once we have everything figured out.
    /// ----------------------

    // Creates a subfolder at the current route with the inputted name
    const createSubfolder = async (name) => {
        if (itemList.includes((item) => item.name === name)) {
            debugLog.log("[DATASTORESV3] Folder creation aborted. Name already exists.");
            createToast("A file or folder already exists at this spot with that name.", "warning");
            return;
        }

        setLoadingText("Creating your subfolder...");
        try {
            debugLog.log("[DATASTOREV3] Attempting to make folder", name, dataStoreId, currFolderId);
            const newFolder = await apiServiceClient.DataStore.createDataStoreFolder(dataStoreId, currFolderId, name);
            debugLog.log("[DATASTORESV3] Folder created successfully.", newFolder);
            // Should be as simple as updating the states if the API returns successfully.
            setItemList([...itemList, {"type": "datastore", "id": newFolder.storageId, "name": newFolder.name}]);
        } catch (err) {
            createToast("There was an error creating your subfolder.", "error");
        } finally {
            setLoadingText("");
        }
    }

    // Attempts to route into a subfolder.
    // TODO: Memoize info so this call does not need to happen twice for the same subfolder.
    const routeIntoSubfolder = async (folderId) => {
        setLoadingText("Loading the details of your subfolder...");
        try {
            // In theory, this returns a list of items that can directly be plugged into an array.
            const newFolderItems = await apiServiceClient.DataStore.getDirectSubFoldersAndFiles(dataStoreId, folderId);
            debugLog.log("[DATASTORESV3] Folder routed into successfully. Child items:", newFolderItems);
            // Update path
            setCurrPath([...currPath, itemList.find((item) => item.id === folderId)])
            // Should be as simple as updating the states if the API returns successfully.
            setItemList([...newFolderItems]);
            setCurrFolderId(folderId);
        } catch (err) {
            createToast("There was an error routing into your subfolder.", "error");
        } finally {
            setLoadingText("");
        }
    }

    // Attempts to delete a folder (as well as all its' files).
    const deleteSubfolder = async (folderId) => {
        setLoadingText("Deleting your subfolder...");
        try {
            // If the backend handles the deletion of all children files, there won't be much here needed.
            await apiServiceClient.DataStore.deleteDataStoreFolder(dataStoreId, folderId);
            debugLog.log("[DATASTORESV3] Folder deleted successfully.");
            // Realistically deletes are only called when the end user is in the parent subfolder. As such, a state update is needed.
            setItemList([...itemList.filter((item) => item.storageId !== folderId)]);
        } catch (err) {
            createToast("There was an error deleting your subfolder.", "error");
        } finally {
            setLoadingText("");
        }
    }

    // Name will update and this will replace the older one in due time. For now, we test.
    const newAddFileToDataStore = async (file, subfolderId = null) => {
        setLoadingText("Uploading your files...")
        try {
            // If upload returns the file object, adding it into the list should be simple. Just make sure you append the subfolder ID.
            const newFile = await apiServiceClient.DataStore.newAddContentToDataStore(dataStoreId, {...file, "subfolderId": subfolderId ?? currFolderId});
            debugLog.log("[DATASTORESV3] File uploaded successfully. Object:", newFile);
            // Realistically uploads are only called when the end user is in the parent subfolder.
            // Because of our file upload behavior in dataStorefileView, a state update is not needed.
            return newFile;
        } catch (err) {
            createToast("There was an error uploading your file.", "error");
        } finally {
            setLoadingText("");
        }
    }

    // Helper function for routing out of a folder instead of in.
    const routeOutOfSubfolder = async (folderId) => {
        let newCurrPath;
        if (folderId === null) {
            newCurrPath = [];
        } else {
            // We will create the ideal currPath array before we do the actual call, and then replace it afterwards if it succeeds.
            const folderIndex = currPath.indexOf((item) => item.id === folderId);
            newCurrPath = [...currPath.slice(0, folderIndex+1)];
        }

        try {
            await routeIntoSubfolder(folderId);
            setCurrPath(newCurrPath);
        } catch (err) {
            debugLog.log("[DATASTOREV3] Error while routing out of a subfolder.");
        }
    }

    // Given a text note's ID, fetch the full object.
    const fetchTextNoteItem = async (contentId) => {
        setLoadingText("Fetching your text note...");
        try {
            const respTextNote = await apiServiceClient.DataStore.fetchDataStoreTextNote(dataStoreId, contentId);
            setTextNote({...respTextNote, "id": contentId});
            debugLog.log("[DATASTOREV3] Recieved text note:", respTextNote);
        } catch (err) {
            debugLog.log("[DATASTOREV3] Error fetching text note...", err);
            createToast("An error occurred while fetching your text note.", "error");
        } finally {
            setLoadingText("");
        }
    }

    const newUploadTextNote = async () => {
        setLoadingText("Creating your text note...");
        try {
            setTextNotes([...textNotes, {...textNote, "isLoading": true, "name": textNote.title}]);
            const newContentId = await apiServiceClient.DataStore.createDataStoreTextNote(dataStoreId, textNote);
            setTextNotes([...textNotes.filter((note) => !note.isLoading), {...textNote, "id": newContentId, "name": textNote.title}]);
            createToast("Text note created successfully.", "success");
        } catch (err) {
            debugLog.log("[DATASTOREV3] Error creating text note...", err);
            createToast("An error occurred while creating your text note.", "error");
            setTextNotes([...textNotes.filter((note) => !note.isLoading)]);
        } finally {
            setLoadingText("");
        }
    }

    const updateTextNote = async () => {
        setLoadingText("Updating your text note...");
        try {
            setTextNotes([...textNotes.map((note) => note.id === textNote.id ? {...note, "isLoading": true, "name": textNote.title} : note)]);
            const newContentId = await apiServiceClient.DataStore.updateDataStoreTextNote(dataStoreId, textNote.id, textNote);
            // Replacing the old text note with the new one. This should hopefully get rid of the loading state too.
            setTextNotes([...textNotes.filter((item) => item.id !== textNote.id), {...textNote, "id": newContentId, "name": textNote.title}]);
            createToast("Text note updated successfully.", "success");
        } catch (err) {
            debugLog.log("[DATASTOREV3] Error updating text note...", err);
            // Removing the loading state for a failed upload.
            setTextNotes([...textNotes.map((note) => note.id === textNote.id ? {...note, "isLoading": false} : note)]);
            createToast("An error occurred while updating your text note.", "error");
        } finally {
            setLoadingText("");
        }
    }

    const deleteTextNote = async (contentId) => {
        setLoadingText("Deleting your text note...");
        try {
            await apiServiceClient.DataStore.removeDataStoreContent(dataStoreId, contentId);
            // Replacing the old text note with the new one.
            setTextNotes([...textNotes.filter((item) => item.id === contentId)]);
        } catch (err) {
            debugLog.log("[DATASTOREV3] Error deleting text note...", err);
            createToast("An error occurred while deleting your text note.", "error");
        } finally {
            setLoadingText("");
        }
    }

    /// --------------------------
    /// Data Store List functions
    /// Functions that apply to the array of data stores.
    /// --------------------------

    // Fetches the list of data stores available to the user.
    const getDataStores = async () => {
        setIsLoading(true);
        try {
            const newDataStores = await apiServiceClient.DataStore.getDataStores();
            setDataStores(newDataStores ?? []);
            setAllDataStores([...newDataStores] ?? []);
            debugLog.log("[DATASTORES] Found data stores!", newDataStores);
        } catch (error) {
            handleApiError("Error when grabbing data stores.", error);
        } finally {
            setIsLoading(false);
        }
    }

    // Searches the available list of data stores with a given query.
    const searchDataStores = async (query, silent = false) => {
        // Handling the event of an empty search query
        if (!query || !query.trim()) {
            setDataStores(allDataStores);
            return;
        }

        // Setting loading state for UI loading components
        if (!silent) setIsLoading(true);

        try {
            debugLog.log("[DATASTORES] Searching data stores with query", query);
            const respDataStores = await apiServiceClient.DataStore.getDataStores(query);
            setDataStores([...respDataStores]);
            debugLog.log("[DATASTORES] Results:", respDataStores);
        } catch (error) {
            handleApiError("Error searching data stores.", error);
        } finally {
            setIsLoading(false);
        }
    }

    // Takes a destination ID and does the necessary jobs to set up the single view page.
    const selectDataStore = async (destDataStoreId) => {
        debugLog.log("[DATASTORES] Entering data store with ID", destDataStoreId);
        setDataStoreId(destDataStoreId);
        await fetchDataStore(destDataStoreId);
        setCurrFolderId(undefined);
        navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/data/${destDataStoreId}`);
    }

    /// --------------------------
    /// Data Store Functions
    /// Functions that apply to the data store object itself.
    /// --------------------------

    // Gets a single data store via a given ID.
    const fetchDataStore = async (dataStoreId) => {
        if (!dataStoreId) return;

        setIsLoading(true);
        try {
            debugLog.log("[DATASTORE] Attempting to fetch data store...")
            const response = await apiServiceClient.DataStore.getDataStore(dataStoreId);
            debugLog.log("[DATASTORE] Recieved data store:", response)

            const { accesses } = response;
            // Updating states
            setDataStore(response);
            // IMPORTANT: Only set folder list on GET, not any other API update. Frontend will handle the rest.
            setFolderList([...new Set(response?.contentItems?.map(file => file.path).filter((p) => p !== null))])
            setSharedUsers(accesses);

            // Base reset the folder and list of files
            setCurrFolderId(undefined);
            setCurrPath([]);

            // All items to be displayed on the file screen are NOT text notes, all that are to be displayed for notes ARE notes.
            setItemList([...(response?.directChildItems?.filter((item) => item.type !== "textnote") ?? [])]);
            setTextNotes([...(response?.directChildItems?.filter((item) => item.type === "textnote") ?? [])]);
        } catch (error) {
            handleApiError("Failed to fetch data store", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Creates a new data store.
    const createDataStore = async (name, description = undefined) => {
        setIsLoading(true);
        debugLog.log("[DATASTORES] Creating a new data store named", name, "...");
        try {
            // Create a temporary data store, the ID being "loadingitem" controls conditional rendering.
            setDataStores([...dataStores, { "id": "loadingitem", "name": name, "description": description }]);
            // Construct the data store object from the name and description
            let result = await apiServiceClient.DataStore.createDataStore({ name: name, description: description });
            // Filter out the temporary data store and add the new fully assembled data store.
            setDataStores([...dataStores.filter((dataStore) => dataStore.id !== "loadingitem"), result]);
            createToast("Data collection created successfully.", "success");
        } catch (error) {
            handleApiError("There was a problem creating your data collection.", error);
        } finally {
            setIsLoading(false);
        }
    }

    // Renames a given data store with a new name.
    const renameDataStore = async (dataStoreId, newName) => {
        debugLog.log("[DATASTORE] Changing name for data store...");
        try {
            let changedDataStore = dataStores.find((dataStore) => dataStore.id === dataStoreId);
            // Local update of the list of data stores
            changedDataStore.name = newName;
            setDataStores([...dataStores]);
            // Backend/API update of the list of data stores
            await apiServiceClient.DataStore.renameDataStore(dataStoreId, newName);
            setDataStoreId(undefined);
        } catch (error) {
            handleApiError("Error renaming DataStore.", error);
        }
    }

    // Changes a given data store's description.
    const changeDataStoreDescription = async (dataStoreId, newDescription) => {
        try {
            debugLog.log("[DATASTORE] Changing description for data store...");
            // Local update of the list of data stores
            let changedDataStore = dataStores.find((dataStore) => dataStore.id === dataStoreId);
            changedDataStore.description = newDescription;
            setDataStores([...dataStores]);
            // Backend/API update of the list of data stores
            await apiServiceClient.DataStore.updateDataStoreDescription(dataStoreId, newDescription);
        } catch (error) {
            handleApiError("Error updating DataStore description.", error);
        }
    }

    // Delete the current data store.
    const deleteDataStore = async () => {
        setIsLoading(true);
        try {
            debugLog.log("[DATASTORES] Attempting to delete data store...")
            await apiServiceClient.DataStore.deleteDataStore(dataStoreId);
            setDataStores(dataStores.filter((dataStore) => dataStore.id !== dataStoreId));
            setAllDataStores(allDataStores.filter((dataStore) => dataStore.id !== dataStoreId));
            setDataStoreId(undefined);
        } catch (error) {
            debugLog.log("[DATASTORES] Error when deleting data store.")
        }
        setIsLoading(false);
    }

    // Toggle the favorite status of the data store.
    const toggleFavoriteDataStore = async (dataStoreId, isFavorited) => {
        try {
            let currDataStore = dataStores.find((dataStore) => dataStore.id === dataStoreId);
            debugLog.log("[DATASTORES] Toggling favorite for ID", dataStoreId, "to", !isFavorited);
            if (isFavorited) {
                await apiServiceClient.DataStore.unfavoriteDataStore(dataStoreId);
            } else {
                await apiServiceClient.DataStore.favoriteDataStore(dataStoreId);
            }
            currDataStore.isFavorited = !currDataStore.isFavorited;
            // Proc update to the data store list to update rendering
            setDataStores([...dataStores]);
            //createToast("Favorite status updated successfully.", "success");
        } catch (error) {
            handleApiError("Error toggling favorite status.", error);
        }
    }

    /// --------------------------
    /// Data Store Share Functions
    /// Functions that relate to sharing or access to the data stores.
    /// --------------------------

    // Get the members of the current data store.
    const getDataStoreMembers = async () => {
        let members = dataStore?.accesses;
        debugLog.log("[ACCESS] Get chat members: ", members);
        setSharedUsers(members ?? []);

        // Sanity check to make sure the user has access to the current data store.
        const membership = members.find((x) => x.userProfileId === userProfile?.userProfileId);

        if (membership) {
            setUsersharedUsership(membership.membership);
        }
        else {
            //It should never be possible to get here, since the backend will only return data stores the user has access to, but including for security sake
            debugLog.error("[DATASTORE] User does not have access to this chat.");
        }

        // Returning object value instead of state in case of race conditions
        return dataStore?.accesses;
    }

    // Adds a user to a data store.
    const shareDataStore = async (user, membership) => {
        if (!dataStoreId) {
            debugLog.error("[SHAREDATASTORE] NO DATASTORE HERE DUDE");
            return;
        }

        if (user) {
            try {
                debugLog.log("[SHAREDATASTORE] Sharing datastore with a user.", user)
                const resp = await apiServiceClient.DataStore.shareDataStore(dataStoreId, user.userProfileId, user.identifier, membership);
                //const resp = await apiServiceClient.DataStore.getActiveUsers(dataStoreId);
                setSharedUsers([...sharedUsers, resp]);
                createToast("DataStore shared successfully.", "success");
            } catch (error) {
                handleApiError("Error sharing DataStore.", error);
            } finally {
                setIsLoading(false);
            }
        }
    }

    // Updates a member's current membership level for a data store.
    const updateDataStoreAccess = async (userId, membership) => {
        debugLog.log("[DATASTORES] test")
        setIsLoading(true);
        try {
            await apiServiceClient.DataStore.updateDataStoreAccess(dataStoreId, userId.userProfileId, membership);
            const response = await apiServiceClient.DataStore.getDataStore(dataStoreId);
            replaceUser(userId.userProfileId, response.accesses);
            createToast("DataStore access updated successfully", "success");
        } catch (error) {
            handleApiError("Error updating DataStore access", error);
        } finally {
            setIsLoading(false);
        }
    };

    // I'm gonna be honest I have no idea why this function exists
    const replaceUser = (userId, accessList) => {
        const updatedUsers = accessList.map((user) => user.userProfileId === userId.userProfileId ? userId : user)
        setSharedUsers(updatedUsers);
    }

    // Removes a user's access to a data store.
    const removeUserDataStoreAccess = async (userId) => {
        setIsLoading(true);
        try {
            await apiServiceClient.DataStore.removeUserDataStoreAccess(dataStoreId, userId.userProfileId);
            const response = await apiServiceClient.DataStore.getDataStore(dataStoreId); //could optimize by creating a getActive users only api call
            setSharedUsers(response.accesses);
            createToast("User access removed successfully", "success");
        } catch (error) {
            handleApiError("Error removing user access", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Check if a user is a member of the current chat
    const isMember = async (user) => {
        debugLog.log("[DATASTORE] " + user.identifier + " being checked for membership");
        await getDataStoreMembers();
        const membership = sharedUsers.find((x) => x.userProfileId === user?.userProfileId);
        return membership;
    }

    // Search all users with a given query, usually a name.
    const searchUsers = async (query) => {
        return await apiServiceClient.UserProfiles.searchUsers(query);
    }

    /// --------------------------
    /// Data Store Content Functions
    /// Functions that apply to the files or notes stored inside the data store.
    /// --------------------------

    // Adds a file to the data store.
    const addContentToCurrentDataStore = async (file) => {
        // setIsLoading(true);
        try {
            const result = await apiServiceClient.DataStore.addContentToDataStore(dataStoreId, file);
            debugLog.log("[DATASTORES] Upload finished. New item:", result);
            if (!result.isSuccess) {
                throw new Error("Internal file upload failure.");
            }
            //createToast("File was added to collection successfully.", "success" );
            return result.result;
        } catch (error) {
            //handleApiError("An error occurred when adding your file. File was not added to collection.", error);
            const errorMessage = error.message || "An error occurred during the file upload.";
            throw new Error(errorMessage);
        }
    };

    // Adds a note to the data store.
    const addNoteToCurrentDataStore = async (name, text) => {
        setIsLoading(true);
        try {
            const result = await apiServiceClient.DataStore.addContentToDataStore(dataStoreId, { resourceName: name, text });
            return result?.result;
        } catch (error) {
            handleApiError("An error occurred when adding your note.", error);
        } finally {
            setIsLoading(false);
        }
    }

    // Fetches and returns the list of content items in a given data store.
    const fetchDataStoreContents = async (dataStoreId) => {
        try {
            const responseContentItems = await apiServiceClient.DataStore.getDataStoreContents(dataStoreId);
            return responseContentItems;
        } catch (error) {
            debugLog.log("[DATASTORE] Error fetching contents.")
        }
    }

    // Fetches and returns a given content item in a given data store.
    const fetchDataStoreContentItem = async (contentItemId) => {
        try {
            const responseContentItem = await apiServiceClient.DataStore.getDataStoreContentItem(dataStoreId, contentItemId);
            return responseContentItem;
        } catch (error) {
            handleApiError("[DATASTORES] Error fetching content item", error);
        }
    };

    // Removes a file or a note from a given data store. Silent flag to not update state.
    const deleteCurrentDataStoreContent = async (contentId, silent = false) => {
        try {
            debugLog.log("[DATASTORES] Deleting a file with ID", contentId);
            await apiServiceClient.DataStore.removeDataStoreContent(dataStoreId, contentId, currFolderId);
            // If we don't care about being silent, update the state.
            // if (!silent) setDataStore((current) => ({ ...current, contentItems: current.contentItems.filter((item) => item.id != contentId)}));
        } catch (error) {
            handleApiError("Error deleting DataStore Content.", error);
        }
    }

    // Deletes a "folder", which is equivalent to deleting all files that begin with a certain path.
    const deleteDataStoreFolder = async (targetPath) => {
        // Sift out all the files that have that path
        let toBeDeleted = dataStore.contentItems.filter((item) => (item.path ?? "").startsWith(targetPath));
        // Delete one by one
        const deleteOperations = toBeDeleted.map(async (file) => {
            // Silent delete so there's only one state update, at the very end.
            // Each state update regenerates the file-tree structure in dataStoreFileView. While not intensive resource-wise, it's probably smart to batch this update.
            deleteCurrentDataStoreContent(file.id, true);
        });

        await Promise.all(deleteOperations);
        // Update the data store to filter those out.
        setDataStore({ ...dataStore, contentItems: dataStore.contentItems.filter((item) => !(item.path ?? "").startsWith(targetPath)) });
    }

    // Renames a file in a given data store.
    const renameCurrentDataStoreItem = async (contentId, newName) => {
        debugLog.log("[DATASTORE] Renaming file...");
        await apiServiceClient.DataStore.renameDataStoreContent(contentId, newName);

        let renamedItem = dataStore.contentItems.find((item) => item.id === contentId);
        if (renamedItem) {
            renamedItem.resourceName = newName;
            setDataStore({ ...dataStore, contentItems: [...dataStore.contentItems.filter((item) => item.id !== contentId), renamedItem] });
        }
    }

    // Try-catch takes place in dataStoreFileView.
    const fetchFileMetadata = async (contentId) => {
        let resp = await apiServiceClient.DataStore.fetchDataStoreItemMetadata(dataStoreId, contentId)
        return resp;
    }


    const updateFileMetadata = async (contentId, metadata) => {
        try {
            debugLog.log("[DATASTORE] Updating metadata for item", contentId, "to", metadata);
            await apiServiceClient.DataStore.updateDataStoreItemMetadata(dataStoreId, contentId, metadata)
        } catch (e) {
            handleApiError('An error occurred while updating file notes.', e)
        }
    }

    // Helper function for printing API errors.
    const handleApiError = (message, error) => {
        debugLog.error(message, error);
        createToast(message, "error");
        setIsLoading(false);
    };

    return (
        <DataStoreContext.Provider
            value={{
                // States
                dataStoreId,
                setDataStoreId,
                dataStores,
                allDataStores,
                dataStore,
                setDataStore,
                folderList,
                setFolderList,
                sharedUsers, //List of users who have access to this, you can revoke and adjust access levels by using sharedUsers props (membership, userprofileid)
                isLoading,

                itemList,
                setItemList,
                textNotes,
                setTextNotes,
                textNote,
                setTextNote,
                currFolderId,
                currPath,
                loadingText,
                // error,
                canShare,
                usersharedUsership,
                membershipLevels,
                // Funcs
                getDataStores,
                searchDataStores,
                selectDataStore,
                fetchDataStore,
                createDataStore,
                renameDataStore,
                changeDataStoreDescription,
                deleteDataStore,
                toggleFavoriteDataStore,
                getDataStoreMembers,
                shareDataStore,
                updateDataStoreAccess,
                replaceUser,
                removeUserDataStoreAccess,
                isMember,
                searchUsers,
                addContentToCurrentDataStore,
                addNoteToCurrentDataStore,
                fetchDataStoreContents,
                fetchDataStoreContentItem,
                deleteCurrentDataStoreContent,
                deleteDataStoreFolder,
                renameCurrentDataStoreItem,
                fetchFileMetadata,
                updateFileMetadata,

                createSubfolder,
                routeIntoSubfolder,
                routeOutOfSubfolder,
                deleteSubfolder,
                newAddFileToDataStore,
                fetchTextNoteItem,
                newUploadTextNote,
                updateTextNote,
                deleteTextNote,
            }}
        >
            {children}
        </DataStoreContext.Provider>
    );
}

function useDataStore() {
    const context = useContext(DataStoreContext);
    if (!context) throw new Error("useDataStore must be used within a DataStoreProvider");
    return context;
}

export { DataStoreProvider, useDataStore };